/**
 * Positions disruptor tiles within the collection product grid.
 *
 * Disruptor tiles are rendered from collection metafield placements into the grid
 * <ul> (after the product loop). Each tile is a grid <li> carrying
 * data-disruptor-position (a 1-based slot, absolute across the collection). This
 * element moves each tile to that slot among the product cards on the current
 * page — using the page offset on the <ul> — and hides tiles whose slot doesn't
 * fall on the currently loaded range. It re-runs after first paint and after every
 * Section Rendering morph (sort/filter) or infinite-scroll append.
 *
 * Positioning is done by DOM insertion (not fixed CSS-Grid coordinates) so it works
 * across this theme's shopper-switchable 1/2/3-column grid. col_span/row_span are
 * applied as grid spans, clamped to the active column count.
 */
if (!customElements.get('disruptor-grid')) {
  customElements.define(
    'disruptor-grid',
    class DisruptorGrid extends HTMLElement {
      /** @type {MutationObserver | undefined} */
      #observer;

      /** @type {ResizeObserver | undefined} */
      #resizeObserver;

      /** @type {number | undefined} */
      #frame;

      /** @type {AbortController | undefined} */
      #listeners;

      /**
       * Sets up observers and schedules the initial tile placement.
       * Disconnects any prior observers first so reconnects after a Section
       * Rendering morph don't leak stale observer instances.
       */
      connectedCallback() {
        this.#observer?.disconnect();
        this.#resizeObserver?.disconnect();
        this.#listeners?.abort();
        if (this.#frame != null) {
          cancelAnimationFrame(this.#frame);
          this.#frame = undefined;
        }

        this.#schedulePlacement();

        // The title/price block height changes when the web font loads, without resizing the
        // grid, so the ResizeObserver below wouldn't re-measure it. The promise outlives this
        // element, so skip it if the grid was removed (morph, filter swap) before fonts resolved.
        document.fonts?.ready.then(() => {
          if (this.isConnected) this.#schedulePlacement();
        });

        // When this grid wraps a <dy-plp> (DY-driven listing), the product cards arrive
        // asynchronously: dy-plp fills the skeleton <li>s in place, which the childList
        // observer below doesn't see. It fires dy-plp:filled once the cards (tagged with
        // data-product-id) are present — re-place then so tiles slot among them.
        this.#listeners = new AbortController();
        this.addEventListener('dy-plp:filled', () => this.#schedulePlacement(), {
          signal: this.#listeners.signal,
        });

        const grid = this.#grid();
        if (!grid) return;

        // Catch infinite-scroll appends (new product cards / re-rendered tiles) and
        // shopper column switches, which flip the product-grid-view attribute without
        // changing the grid's size (so the ResizeObserver below wouldn't fire).
        this.#observer = new MutationObserver(() => this.#schedulePlacement());
        this.#observer.observe(grid, this.#observerConfig);

        // Re-clamp column spans when the grid resizes (breakpoint / column-count
        // change via the product-grid-view switch).
        this.#resizeObserver = new ResizeObserver(() => this.#schedulePlacement());
        this.#resizeObserver.observe(grid);
      }

      /** Cancels any pending rAF and disconnects all observers. */
      disconnectedCallback() {
        this.#observer?.disconnect();
        this.#resizeObserver?.disconnect();
        this.#listeners?.abort();
        if (this.#frame != null) {
          cancelAnimationFrame(this.#frame);
          this.#frame = undefined;
        }
      }

      /**
       * Watch child insertions (infinite scroll, morphs) and the product-grid-view
       * attribute (column switches).
       * @type {MutationObserverInit}
       */
      #observerConfig = { childList: true, attributes: true, attributeFilter: ['product-grid-view'] };

      /** @returns {HTMLElement | null} The product grid list element. */
      #grid() {
        return this.querySelector('.product-grid');
      }

      /**
       * @param {HTMLElement} grid - The product grid list element.
       * @returns {number} The active column count read from the computed grid template.
       */
      #columnCount(grid) {
        const template = getComputedStyle(grid).gridTemplateColumns;
        // Split on whitespace that is not inside parentheses to correctly handle
        // resolved minmax() track values (e.g. "minmax(100px, 1fr) minmax(100px, 1fr)"
        // is 2 tracks, not 4 tokens).
        const count = template.split(/\s+(?![^(]*\))/).filter((t) => t.trim() !== '').length;
        return count > 0 ? count : 1;
      }

      /** Debounces placement to one run per frame. */
      #schedulePlacement() {
        if (this.#frame != null) cancelAnimationFrame(this.#frame);
        this.#frame = requestAnimationFrame(() => {
          this.#frame = undefined;
          this.#place();
        });
      }

      /** Moves each disruptor tile to its slot on the current page (or hides it). */
      #place() {
        const grid = this.#grid();
        if (!grid) return;

        // Pause the observer while mutating so our own moves don't re-trigger it.
        // Wrapped in try/finally so a throw during placement always restores the observer.
        this.#observer?.disconnect();
        try {
          this.#doPlace(grid);
        } finally {
          const currentGrid = this.#grid();
          if (currentGrid) this.#observer?.observe(currentGrid, this.#observerConfig);
        }
      }

      /**
       * Inner placement logic, separated so #place can wrap it in try/finally.
       * @param {HTMLElement} grid - The product grid list element.
       */
      #doPlace(grid) {
        const offset = parseInt(grid.getAttribute('data-disruptor-offset') ?? '0', 10) || 0;
        const designMode = Boolean(window.Shopify?.designMode);

        // Drop duplicate tiles (same id) left behind by re-renders / appends.
        // Skip removal in design mode: the duplicate may be the merchant's currently-
        // selected tile in the editor, and removing it would lose their selection.
        if (!designMode) {
          const seen = new Set();
          for (const tile of grid.querySelectorAll(':scope > .collection-grid__disruptor-tile')) {
            if (tile.id && seen.has(tile.id)) {
              tile.remove();
            } else if (tile.id) {
              seen.add(tile.id);
            }
          }
        }

        const tiles = grid.querySelectorAll(':scope > .collection-grid__disruptor-tile');
        const cards = grid.querySelectorAll(':scope > .product-grid__item[data-product-id]');

        // Neutralise every tile (single column, no breakout) BEFORE reading the column
        // count. A tile whose span exceeds the explicit track count makes the browser add
        // implicit columns, which Chrome then reports in gridTemplateColumns — so a stale
        // wide span (or a leftover full-width breakout from a previous view) would inflate
        // the count and prevent clamping. Reset first, then measure the true count.
        for (const tile of tiles) this.#resetTile(tile);
        const columns = this.#columnCount(grid);

        // Mirror a product card's height + the grid row gap so a disruptor tile fills the
        // same vertical space as the products around it (× row_span for taller tiles),
        // even in a row it doesn't share with a product card. Falls back to aspect-ratio
        // on mobile (see CSS).
        const sampleCard = cards[0];
        if (sampleCard) {
          const rowGap = parseFloat(getComputedStyle(grid).rowGap) || 0;
          // Collapse our own card-height contribution BEFORE sampling. A disruptor tile
          // sharing the sample card's grid row would otherwise stretch that row to the
          // tile's own (stale) height — we'd then read that inflated height back and write
          // it straight into the var, locking it in. The tile then never resizes when the
          // column count changes (e.g. 2→3 moves a position-3 tile into row 1 with the
          // sample card) until a full reload. Zeroing first lets the shared row collapse to
          // the true card height before we measure it.
          this.style.setProperty('--disruptor-card-height', '0px');
          this.style.setProperty('--disruptor-card-text-height', '0px');
          const cardRect = sampleCard.getBoundingClientRect();
          this.style.setProperty('--disruptor-card-height', `${cardRect.height}px`);
          this.style.setProperty('--disruptor-row-gap', `${rowGap}px`);

          // The title/price block sits below the product image. A tile has no such block, so
          // CSS subtracts it to end the tile on the image's bottom edge, not the card's. Measured
          // from the image's bottom edge so the card's top padding isn't subtracted as well.
          // Depends on the product card markup: if `.card-gallery` is renamed this falls back to 0
          // and the tile reverts to the full card height.
          const cardGallery = sampleCard.querySelector('.card-gallery');
          const cardTextHeight = cardGallery ? cardRect.bottom - cardGallery.getBoundingClientRect().bottom : 0;
          this.style.setProperty('--disruptor-card-text-height', `${cardTextHeight}px`);

          // Mirror the product card's top padding (a merchant block setting on
          // .product-card__content, above the product image) so a disruptor's content lines
          // up with the product images beside it rather than sitting flush to the tile top.
          const cardContent = sampleCard.querySelector('.product-card__content');
          if (cardContent) {
            this.style.setProperty(
              '--disruptor-offset-block-start',
              getComputedStyle(cardContent).paddingBlockStart
            );
          }
        }

        for (const tile of tiles) {
          const position = parseInt(tile.getAttribute('data-disruptor-position') ?? '0', 10);
          // Slot on the loaded range: 1-based index among the product cards.
          const slot = position - offset;
          const onThisPage = position > 0 && slot >= 1 && slot <= cards.length;

          this.#applySpans(tile, columns);

          if (onThisPage) {
            const reference = cards[slot - 1];
            if (tile.nextElementSibling !== reference) {
              // Preserve keyboard focus if the tile (or a descendant) is focused — DOM
              // moves during infinite-scroll appends would otherwise silently reset the
              // tab order from the user's current position.
              const hasFocus = tile.contains(document.activeElement);
              grid.insertBefore(tile, reference);
              if (hasFocus) /** @type {HTMLElement} */ (document.activeElement)?.focus();
            }
            tile.classList.remove('disruptor-tile--pending');
          } else if (designMode) {
            // Keep it visible in the editor so the merchant can see and select it.
            grid.appendChild(tile);
            tile.classList.remove('disruptor-tile--pending');
          } else {
            tile.classList.add('disruptor-tile--pending');
          }
        }
      }

      /**
       * Clears the inline spanning/breakout styles so the tile occupies one column.
       * @param {Element} tile - The disruptor tile element to reset.
       */
      #resetTile(tile) {
        tile.style.setProperty('--disruptor-col-span', '1');
        tile.style.gridColumn = '';
        tile.style.width = '';
        tile.style.justifySelf = '';
        tile.classList.remove('disruptor-tile--full-width');
      }

      /**
       * Applies the tile's column/row span. A tile whose configured col_span fits the
       * active column count spans that many tracks. A tile that wants to be wider than the
       * grid currently provides (col_span > columns) breaks out to the full grid width
       * instead of collapsing — honouring the merchant's "wide tile" intent. The breakout
       * uses the product-grid container inline size (100cqi) so it reaches full width even
       * in grid-of-1, where the single product track is deliberately half-width + centred.
       * @param {Element} tile - The disruptor tile element.
       * @param {number} columns - Active column count of the product grid.
       */
      #applySpans(tile, columns) {
        const colSpan = parseInt(tile.getAttribute('data-col-span') ?? '1', 10) || 1;
        const rowSpan = parseInt(tile.getAttribute('data-row-span') ?? '1', 10) || 1;
        tile.style.setProperty('--disruptor-row-span', String(Math.max(1, rowSpan)));

        if (colSpan > columns) {
          tile.style.setProperty('--disruptor-col-span', String(columns));
          tile.style.gridColumn = '1 / -1';
          tile.style.width = '100cqi';
          tile.style.justifySelf = 'center';
          // Mark as a mobile banner (configurable aspect ratio) only when it's genuinely
          // wider than its neighbours. In a single-column grid every tile is full-width, so
          // a wide tile isn't visually distinct — it should match the surrounding card
          // height like the rest, not shrink to the banner ratio. (The width breakout above
          // still applies so it fills the row on desktop's half-width single column.)
          if (columns > 1) tile.classList.add('disruptor-tile--full-width');
        } else {
          tile.style.setProperty('--disruptor-col-span', String(Math.max(1, colSpan)));
        }
      }
    }
  );
}
